import * as React from 'react';
import { Text, Icon } from '../../display';
import { ChevronDown, Check, Search } from 'lucide-react';
import { InputMessage } from '../shared';

/**
 * Size variants for the Select
 * - sm: 32px (compact)
 * - md: 40px (default)
 * - lg: 48px (comfortable)
 */
export type SelectSize = 'sm' | 'md' | 'lg';

/**
 * Option type for Select items
 */
export interface SelectOption {
  /**
   * The value of the option (used for form submission)
   */
  value: string;
  /**
   * The display label for the option
   */
  label: string;
  /**
   * Whether the option is disabled
   */
  disabled?: boolean;
}

/**
 * Select component props
 */
export interface SelectProps {
  /**
   * Array of options to display in the dropdown
   */
  options: SelectOption[];
  /**
   * Current value (string for single-select, string[] for multi-select)
   */
  value?: string | string[];
  /**
   * Callback when value changes
   */
  onChange?: (value: string | string[]) => void;
  /**
   * Label text displayed above the select
   */
  label?: string;
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Helper text displayed below the select
   */
  helperText?: string;
  /**
   * Whether the select is in an error state
   */
  error?: boolean;
  /**
   * Error message displayed when error is true
   */
  errorMessage?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Whether the select is disabled
   */
  disabled?: boolean;
  /**
   * Size variant of the select
   * @default 'md'
   */
  size?: SelectSize;
  /**
   * Enable multi-select mode
   */
  multiple?: boolean;
  /**
   * Enable search/filter functionality
   */
  searchable?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Custom ID for the select
   */
  id?: string;
  /**
   * Name attribute for form submission
   */
  name?: string;
  /**
   * Callback when select receives focus
   */
  onFocus?: (event: React.FocusEvent) => void;
  /**
   * Callback when select loses focus
   */
  onBlur?: (event: React.FocusEvent) => void;
}

/**
 * Height classes for each size variant
 */
const sizeHeightClasses: Record<SelectSize, string> = {
  sm: 'h-8', // 32px
  md: 'h-10', // 40px
  lg: 'h-12', // 48px
};

/**
 * Icon sizes for each select size
 */
const iconSizes: Record<SelectSize, 'xs' | 'sm' | 'md'> = {
  sm: 'xs',
  md: 'sm',
  lg: 'md',
};

/**
 * Find the next enabled option index moving forward
 */
function findNextEnabledIndex(
  options: SelectOption[],
  currentIndex: number
): number {
  let newIndex = currentIndex + 1;
  while (newIndex < options.length && options[newIndex]?.disabled) {
    newIndex++;
  }
  return newIndex < options.length ? newIndex : currentIndex;
}

/**
 * Find the next enabled option index moving backward
 */
function findPrevEnabledIndex(
  options: SelectOption[],
  currentIndex: number
): number {
  let newIndex = currentIndex - 1;
  while (newIndex >= 0 && options[newIndex]?.disabled) {
    newIndex--;
  }
  return newIndex >= 0 ? newIndex : currentIndex;
}

/**
 * Find the first enabled option index
 */
function findFirstEnabledIndex(options: SelectOption[]): number {
  return options.findIndex((o) => !o.disabled);
}

/**
 * Find the last enabled option index
 */
function findLastEnabledIndex(options: SelectOption[]): number {
  for (let i = options.length - 1; i >= 0; i--) {
    const option = options[i];
    if (option && !option.disabled) {
      return i;
    }
  }
  return -1;
}

/**
 * Find next option starting with a character (type-to-search)
 */
function findOptionByChar(
  options: SelectOption[],
  char: string,
  startIndex: number
): number {
  const lowerChar = char.toLowerCase();
  for (let i = 0; i < options.length; i++) {
    const index = (startIndex + 1 + i) % options.length;
    const option = options[index];
    if (option && !option.disabled && option.label.toLowerCase().startsWith(lowerChar)) {
      return index;
    }
  }
  return -1;
}

/**
 * Base trigger classes that are always applied
 */
const baseTriggerClasses = [
  'w-full',
  'rounded-[6px]',
  'border',
  'px-3',
  'pr-10',
  'text-[15px]',
  'leading-[22px]',
  'outline-none',
  'transition-all',
  'duration-150',
  'ease-out',
  'text-left',
  'flex',
  'items-center',
  'justify-between',
].join(' ');

/**
 * Get state-specific trigger classes
 */
function getTriggerStateClasses(error: boolean, disabled: boolean): string {
  if (disabled) {
    return 'bg-secondary-100 text-secondary-400 border-secondary-300 cursor-not-allowed';
  }
  if (error) {
    return 'border-error-700 bg-secondary-50 focus:ring-2 focus:ring-error-700/20';
  }
  return 'border-secondary-300 bg-secondary-50 focus:border-primary-400 focus:ring-2 focus:ring-primary-200';
}

/**
 * Base option classes that are always applied
 */
const baseOptionClasses = [
  'px-3',
  'py-2.5',
  'cursor-pointer',
  'flex',
  'items-center',
  'justify-between',
  'text-[15px]',
  'leading-[22px]',
].join(' ');

/**
 * Get state-specific option classes
 */
function getOptionStateClasses(
  isFocused: boolean,
  isSelectedOption: boolean,
  isDisabled: boolean
): string {
  const classes: string[] = [];

  if (isSelectedOption) {
    classes.push('bg-primary-100');
  } else if (isFocused) {
    classes.push('bg-primary-50');
  } else {
    classes.push('hover:bg-primary-50');
  }

  if (isDisabled) {
    classes.push('opacity-50 cursor-not-allowed hover:bg-transparent');
  }

  return classes.join(' ');
}

/**
 * Select component for choosing options from a dropdown menu.
 *
 * Built following the design system specifications:
 * - Height: 40px (default), 32px (compact), 48px (comfortable)
 * - Border: 1px neutral-300 (#D4D1CC)
 * - Border radius: 6px
 * - Chevron icon: 16px, 12px from right edge
 * - Dropdown shadow: shadow-lg
 * - Focus: 2px ring primary-200 (#BCCCDC), border primary-400 (#829AB1)
 * - Error: Border error (#B91C1C), ring error/20%
 * - Disabled: Background neutral-100 (#F5F4F2), text neutral-400 (#A8A29E)
 *
 * @example
 * // Basic usage
 * <Select
 *   options={[{ value: 'a', label: 'Option A' }]}
 *   placeholder="Select an option"
 * />
 *
 * @example
 * // Multi-select
 * <Select
 *   options={options}
 *   multiple
 *   value={['a', 'b']}
 *   onChange={(values) => console.log(values)}
 * />
 *
 * @example
 * // Searchable
 * <Select
 *   options={options}
 *   searchable
 *   placeholder="Search and select..."
 * />
 */
const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      label,
      placeholder = 'Select an option',
      helperText,
      error = false,
      errorMessage,
      required = false,
      disabled = false,
      size = 'md',
      multiple = false,
      searchable = false,
      className = '',
      id: providedId,
      name,
      onFocus,
      onBlur,
    },
    ref
  ) => {
    // State
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [focusedIndex, setFocusedIndex] = React.useState(-1);

    // Refs
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const optionRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    // Merge refs
    React.useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);

    // Generate stable IDs for accessibility
    const reactId = React.useId();
    const selectId = providedId || `select-${reactId}`;
    const helperId = `select-helper-${reactId}`;
    const listboxId = `select-listbox-${reactId}`;

    // Determine message ID for aria-describedby
    const message = (error && errorMessage) ? errorMessage : helperText;
    const messageId = message ? helperId : undefined;

    // Normalize value to array for consistent handling
    const selectedValues = React.useMemo(() => {
      if (value === undefined) return [];
      return Array.isArray(value) ? value : [value];
    }, [value]);

    // Filter options based on search query
    const filteredOptions = React.useMemo(() => {
      if (!searchQuery) return options;
      const query = searchQuery.toLowerCase();
      return options.filter((option) =>
        option.label.toLowerCase().includes(query)
      );
    }, [options, searchQuery]);

    // Get display text for the trigger
    const displayText = React.useMemo(() => {
      if (selectedValues.length === 0) return '';
      if (multiple) {
        if (selectedValues.length === 1) {
          const option = options.find((o) => o.value === selectedValues[0]);
          return option?.label || '';
        }
        return `${selectedValues.length} selected`;
      }
      const option = options.find((o) => o.value === selectedValues[0]);
      return option?.label || '';
    }, [selectedValues, options, multiple]);

    // Check if an option is selected
    const isSelected = React.useCallback(
      (optionValue: string) => selectedValues.includes(optionValue),
      [selectedValues]
    );

    // Handle option selection
    const handleSelect = React.useCallback(
      (optionValue: string) => {
        if (!onChange) return;

        if (multiple) {
          const newValues = isSelected(optionValue)
            ? selectedValues.filter((v) => v !== optionValue)
            : [...selectedValues, optionValue];
          onChange(newValues);
        } else {
          onChange(optionValue);
          setIsOpen(false);
          setSearchQuery('');
          triggerRef.current?.focus();
        }
      },
      [onChange, multiple, selectedValues, isSelected]
    );

    // Handle dropdown toggle
    const toggleDropdown = React.useCallback(() => {
      /* v8 ignore start */
      if (disabled) return;
      /* v8 ignore stop */
      setIsOpen((prev) => {
        const newState = !prev;
        if (newState) {
          setFocusedIndex(-1);
        } else {
          setSearchQuery('');
        }
        return newState;
      });
    }, [disabled]);

    // Close dropdown
    const closeDropdown = React.useCallback(() => {
      setIsOpen(false);
      setSearchQuery('');
      setFocusedIndex(-1);
      triggerRef.current?.focus();
    }, []);

    // Handle click outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          isOpen &&
          !triggerRef.current?.contains(target) &&
          !dropdownRef.current?.contains(target)
        ) {
          setIsOpen(false);
          setSearchQuery('');
          setFocusedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }, [isOpen]);

    // Focus search input when dropdown opens
    React.useEffect(() => {
      if (isOpen && searchable) {
        // Small delay to ensure DOM is ready
        requestAnimationFrame(() => {
          searchInputRef.current?.focus();
        });
      }
    }, [isOpen, searchable]);

    // Scroll focused option into view
    React.useEffect(() => {
      if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
        // scrollIntoView may not be available in test environments
        optionRefs.current[focusedIndex]?.scrollIntoView?.({
          block: 'nearest',
        });
      }
    }, [focusedIndex]);

    // Handle Enter or Space key
    const handleEnterOrSpace = React.useCallback(
      (event: React.KeyboardEvent) => {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
          return;
        }
        if (focusedIndex < 0) return;
        const option = filteredOptions[focusedIndex];
        /* v8 ignore start */
        if (option && !option.disabled) {
          handleSelect(option.value);
        }
        /* v8 ignore stop */
      },
      [isOpen, focusedIndex, filteredOptions, handleSelect]
    );

    // Handle ArrowDown key
    const handleArrowDown = React.useCallback(
      (event: React.KeyboardEvent) => {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
          return;
        }
        setFocusedIndex((prev) => findNextEnabledIndex(filteredOptions, prev));
      },
      [isOpen, filteredOptions]
    );

    // Handle ArrowUp key
    const handleArrowUp = React.useCallback(
      (event: React.KeyboardEvent) => {
        event.preventDefault();
        if (!isOpen) return;
        setFocusedIndex((prev) => findPrevEnabledIndex(filteredOptions, prev));
      },
      [isOpen, filteredOptions]
    );

    // Handle Home key
    const handleHome = React.useCallback(
      (event: React.KeyboardEvent) => {
        if (!isOpen) return;
        event.preventDefault();
        const firstIndex = findFirstEnabledIndex(filteredOptions);
        if (firstIndex >= 0) {
          setFocusedIndex(firstIndex);
        }
      },
      [isOpen, filteredOptions]
    );

    // Handle End key
    const handleEnd = React.useCallback(
      (event: React.KeyboardEvent) => {
        if (!isOpen) return;
        event.preventDefault();
        const lastIndex = findLastEnabledIndex(filteredOptions);
        if (lastIndex >= 0) {
          setFocusedIndex(lastIndex);
        }
      },
      [isOpen, filteredOptions]
    );

    // Handle type-to-search
    const handleTypeToSearch = React.useCallback(
      (event: React.KeyboardEvent) => {
        if (searchable || !isOpen || event.key.length !== 1) return;
        const foundIndex = findOptionByChar(filteredOptions, event.key, focusedIndex);
        if (foundIndex >= 0) {
          setFocusedIndex(foundIndex);
        }
      },
      [searchable, isOpen, filteredOptions, focusedIndex]
    );

    // Handle keyboard navigation
    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent) => {
        if (disabled) return;

        const keyHandlers: Record<string, (e: React.KeyboardEvent) => void> = {
          Enter: handleEnterOrSpace,
          ' ': handleEnterOrSpace,
          ArrowDown: handleArrowDown,
          ArrowUp: handleArrowUp,
          Escape: (e) => {
            e.preventDefault();
            closeDropdown();
          },
          Tab: () => {
            if (isOpen) closeDropdown();
          },
          Home: handleHome,
          End: handleEnd,
        };

        const handler = keyHandlers[event.key];
        if (handler) {
          handler(event);
        } else {
          handleTypeToSearch(event);
        }
      },
      [
        disabled,
        isOpen,
        handleEnterOrSpace,
        handleArrowDown,
        handleArrowUp,
        handleHome,
        handleEnd,
        handleTypeToSearch,
        closeDropdown,
      ]
    );

    // Handle search input Enter key
    const handleSearchEnter = React.useCallback(
      (event: React.KeyboardEvent) => {
        event.preventDefault();
        if (focusedIndex < 0) return;
        const option = filteredOptions[focusedIndex];
        /* v8 ignore start */
        if (option && !option.disabled) {
          handleSelect(option.value);
        }
        /* v8 ignore stop */
      },
      [focusedIndex, filteredOptions, handleSelect]
    );

    // Handle search input keyboard
    const handleSearchKeyDown = React.useCallback(
      (event: React.KeyboardEvent) => {
        const searchKeyHandlers: Record<string, (e: React.KeyboardEvent) => void> = {
          ArrowDown: (e) => {
            e.preventDefault();
            setFocusedIndex((prev) => findNextEnabledIndex(filteredOptions, prev));
          },
          ArrowUp: (e) => {
            e.preventDefault();
            setFocusedIndex((prev) => findPrevEnabledIndex(filteredOptions, prev));
          },
          Enter: handleSearchEnter,
          Escape: (e) => {
            e.preventDefault();
            closeDropdown();
          },
          Tab: () => closeDropdown(),
        };

        const handler = searchKeyHandlers[event.key];
        if (handler) {
          handler(event);
        }
      },
      [filteredOptions, handleSearchEnter, closeDropdown]
    );

    // Build trigger classes using extracted helpers
    const triggerClasses = [
      sizeHeightClasses[size],
      baseTriggerClasses,
      getTriggerStateClasses(error, disabled),
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Option classes using extracted helpers
    const getOptionClasses = (index: number, option: SelectOption) => {
      const selected = isSelected(option.value);
      const focused = index === focusedIndex;
      return `${baseOptionClasses} ${getOptionStateClasses(focused, selected, !!option.disabled)}`;
    };

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <Text
            as="label"
            htmlFor={selectId}
            size="sm"
            weight="medium"
            className="mb-1.5 block text-secondary-700"
          >
            {label}
            {required && (
              <span className="ml-0.5 text-error-700" aria-hidden="true">
                *
              </span>
            )}
          </Text>
        )}

        {/* Select container */}
        <div className="relative">
          {/* Trigger button */}
          <button
            ref={triggerRef}
            type="button"
            id={selectId}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-required={required}
            aria-invalid={error}
            aria-describedby={messageId}
            disabled={disabled}
            onClick={toggleDropdown}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={(e) => {
              // Don't trigger blur if clicking inside dropdown
              if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
                onBlur?.(e);
              }
            }}
            className={triggerClasses}
          >
            <span
              className={`truncate ${
                displayText
                  ? 'text-secondary-800'
                  : 'text-secondary-400'
              }`}
            >
              {displayText || placeholder}
            </span>
            <span className="pointer-events-none absolute right-3 flex items-center">
              <Icon
                icon={ChevronDown}
                size={iconSizes[size]}
                className={`transition-transform duration-150 ${
                  isOpen ? 'rotate-180' : ''
                } ${disabled ? 'text-secondary-400' : 'text-secondary-500'}`}
              />
            </span>
          </button>

          {/* Hidden input for form submission */}
          {name && (
            <input
              type="hidden"
              name={name}
              value={multiple ? selectedValues.join(',') : selectedValues[0] || ''}
            />
          )}

          {/* Dropdown */}
          {isOpen && (
            <div
              ref={dropdownRef}
              className="absolute z-50 mt-1 w-full rounded-lg border border-secondary-200 bg-secondary-100 text-secondary-800 shadow-lg"
              style={{ maxHeight: '280px' }}
            >
              {/* Search input */}
              {searchable && (
                <div className="border-b border-secondary-200 p-2">
                  <div className="relative">
                    <Icon
                      icon={Search}
                      size="sm"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-500"
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setFocusedIndex(-1);
                      }}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="Search..."
                      className="w-full rounded-md border border-secondary-300 bg-secondary-50 py-1.5 pl-9 pr-3 text-[15px] text-secondary-800 placeholder:text-secondary-500 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      aria-label="Search options"
                    />
                  </div>
                </div>
              )}

              {/* Options list */}
              <div
                id={listboxId}
                role="listbox"
                aria-multiselectable={multiple}
                className="overflow-auto"
                style={{ maxHeight: searchable ? '224px' : '280px' }}
              >
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2.5 text-[15px] text-secondary-500">
                    No options found
                  </div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <div
                      key={option.value}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      role="option"
                      tabIndex={-1}
                      aria-selected={isSelected(option.value)}
                      aria-disabled={option.disabled}
                      id={`${listboxId}-option-${index}`}
                      onClick={() => {
                        if (!option.disabled) {
                          handleSelect(option.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !option.disabled) {
                          e.preventDefault();
                          handleSelect(option.value);
                        }
                      }}
                      onMouseEnter={() => {
                        if (!option.disabled) {
                          setFocusedIndex(index);
                        }
                      }}
                      className={getOptionClasses(index, option)}
                    >
                      <span>{option.label}</span>
                      {isSelected(option.value) && (
                        <Icon
                          icon={Check}
                          size="sm"
                          className="text-primary-700"
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Helper text or error message */}
        <InputMessage error={error} errorMessage={errorMessage} helperText={helperText} helperId={helperId} />
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
